import { createContext, useEffect, useReducer, useCallback, useMemo } from "react";
import axios from "axios";
// CUSTOM COMPONENT
import { MatxLoading } from "app/components";

const initialState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false,
  persist: false,
  roles: [5150, 1984, 2001],
  permissions: [201],
  accessToken: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user, permissions, accessToken } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
        permissions: permissions || [],
        accessToken,
      };
    }

    case "LOGIN":
    case "SET_AUTH": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        permissions: action.payload.permissions || [],
        accessToken: action.payload.accessToken,
        persist: true,
      };
    }

    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        persist: false,
        permissions: [],
        accessToken: null,
      };
    }

    case "REGISTER": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: true, user };
    }

    default:
      return state;
  }
};

function safeJSONParse(key) {
  try {
    const item = localStorage.getItem(key);
    if (!item || item === "undefined" || item === "null") return null;
    return JSON.parse(item);
  } catch (err) {
    console.warn(`Safe storage parse error for key "${key}":`, err);
    return null;
  }
}

const AuthContext = createContext({
  ...initialState,
  login: () => {},
  logout: () => {},
  verifyOTP: () => {},
  setAuth: () => {},
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Rehydrate auth state safely from localStorage on mount
  useEffect(() => {
    const storedPersist = safeJSONParse("persist");
    const storedUser = safeJSONParse("user");
    const storedPermissions = safeJSONParse("permissions");
    const storedToken = localStorage.getItem("accessToken");

    if (storedPersist && storedUser && storedToken) {
      dispatch({
        type: "INIT",
        payload: {
          isAuthenticated: true,
          user: storedUser,
          permissions: storedPermissions || [],
          accessToken: storedToken,
        },
      });
    } else {
      dispatch({
        type: "INIT",
        payload: { isAuthenticated: false, user: null },
      });
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(
      "https://backend.dahiyainsurance.com/Login.php",
      { Email: email, Password: password }
    );
    return response;
  }, []);

  const verifyOTP = useCallback(async (email, password, OTP, remember) => {
    const response = await axios.post(
      "https://backend.dahiyainsurance.com/VerifyOTP.php",
      { Email: email, Password: password, OTP },
      { withCredentials: true }
    );

    const result = response.data;
    const user = result.Data;
    const permissions = result.Permissions;
    const accessToken = result.Data?.SessionID;

    localStorage.setItem("persist", remember);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("permissions", JSON.stringify(permissions));
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    }
    localStorage.setItem("tokenExpiry", Date.now() + 15 * 60 * 1000);

    dispatch({ type: "LOGIN", payload: { user, permissions, accessToken } });
    return result;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("persist");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("tokenExpiry");
    dispatch({ type: "LOGOUT" });
  }, []);

  const setAuth = useCallback(({ user, roles, permissions, accessToken }) => {
    dispatch({ type: "SET_AUTH", payload: { user, roles, permissions, accessToken } });
  }, []);

  const contextValue = useMemo(
    () => ({
      ...state,
      login,
      logout,
      verifyOTP,
      setAuth,
      dispatch,
    }),
    [state, login, logout, verifyOTP, setAuth]
  );

  // Show global loader until auth state is rehydrated
  if (!state.isInitialized) return <MatxLoading />;

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
